import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  Paper,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  LinearProgress,
  IconButton,
  Grid,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  ArrowLeft,
  FolderOpen,
  Package,
  CheckCircle2,
  CloudUpload,
  FileVideo,
  Trash2,
  Eye,
  Download,
  History,
  CalendarDays,
  FileText,
} from "lucide-react";

import { Link, useSearchParams } from "react-router-dom";
import CameraModal from "../components/CameraModel";
import axiosInstance from "../api/axiosInstance";
import axios from "axios";

const humanFileSize = (size) => {
  if (!size && size !== 0) return "";
  const i = Math.floor(Math.log(size) / Math.log(1024));
  return (
    (size / Math.pow(1024, i)).toFixed(2) * 1 +
    " " +
    ["B", "KB", "MB", "GB", "TB"][i]
  );
};

const getQuarter = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const quarter = Math.ceil(month / 3);
  return `Q${quarter} ${year}`;
};
export default function UploadPage() {
  const [searchParams] = useSearchParams();
  const queryDistrict = searchParams.get("district") || "";
  const queryPkg = searchParams.get("pkg") || "";
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [district, setDistrict] = useState("");
  const [pkg, setPkg] = useState("");
  const [uploadDate, setUploadDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCameraForModal, setSelectedCameraForModal] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const dropRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          "/api/site/hierarchy?includeAssets=true"
        );

        const apiData = response.data;

        if (mounted) {
          const loadedDistricts = [];
          const loadedPackages = [];

          apiData.forEach((site) => {
            const siteId = site.id.toString();
            loadedDistricts.push({
              id: siteId,
              name: site.name,
            });

            if (site.children && Array.isArray(site.children)) {
              site.children.forEach((childPkg) => {
                loadedPackages.push({
                  id: childPkg.id.toString(),
                  displayName: childPkg.name,
                  districtId: siteId,
                });
              });
            }
          });

          setDistricts(loadedDistricts);
          setPackages(loadedPackages);
        }
      } catch (err) {
        console.error("Failed to load config:", err);
        if (mounted) setLoadError("Failed to load site configuration.");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!loading && districts.length > 0) {
      if (queryDistrict) setDistrict(queryDistrict.toString());
      if (queryPkg) setPkg(queryPkg.toString());
    }
  }, [loading, districts, queryDistrict, queryPkg]);

  const fetchHistory = useCallback(async () => {
    if (!pkg) {
      setUploadHistory([]);
      return;
    }
    try {
      const res = await axiosInstance.get(`/api/pre-check/site/${pkg}`);

      const historyData = res.data.map((item) => ({
        id: item.id,
        name: item.fileName,
        date: item.videoDate,
        description: item.description,
        url:
          item.s3BucketURL ||
          (item.attachment ? item.attachment.s3BucketURL : null),
        status: "success",
        size: 0,
        packageCode: pkg,
        districtId: district,
      }));
      console.log("Fetched history data:", historyData);

      historyData.sort((a, b) => b.id - a.id);
      setUploadHistory(historyData);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  }, [pkg, district]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const uploadFile = useCallback(
    async (fileItem) => {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileItem.id ? { ...f, status: "uploading" } : f
        )
      );

      try {
        const preSignRes = await axiosInstance.post(
          "/api/document/pre-signed-url",
          {
            assetId: pkg,
            fileName: fileItem.name,
          }
        );
        const { preSignedUrl, s3Key } = preSignRes.data;

        await axios.put(preSignedUrl, fileItem.file, {
          headers: { "Content-Type": fileItem.file.type },
          onUploadProgress: (progressEvent) => {
            if (progressEvent && progressEvent.total > 0) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === fileItem.id ? { ...f, progress: percent } : f
                )
              );
            }
          },
        });

        await axiosInstance.post("/api/pre-check/video", {
          siteId: pkg,
          videoDate: uploadDate,
          fileName: fileItem.name,
          description: description || "No description",
          s3Link: s3Key,
        });

        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileItem.id
              ? { ...f, status: "success", progress: 100 }
              : f
          )
        );

        fetchHistory();
        setToast({
          open: true,
          message: `Successfully uploaded ${fileItem.name}`,
          severity: "success",
        });
        setTimeout(() => {
          setFiles((prev) => prev.filter((f) => f.id !== fileItem.id));
        }, 1500);
      } catch (error) {
        console.error("Upload failed:", error);
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileItem.id ? { ...f, status: "error", progress: 0 } : f
          )
        );
        setToast({
          open: true,
          message: `Failed to upload ${fileItem.name}`,
          severity: "error",
        });
      }
    },
    [pkg, uploadDate, description, fetchHistory]
  );

  const handleFileSelection = useCallback(
    async (selectedFiles) => {
      if (!pkg) {
        setToast({
          open: true,
          message: "Please select a package before uploading.",
          severity: "warning",
        });
        return;
      }
      const targetQuarter = getQuarter(uploadDate);
      const existingVideoInQuarter = uploadHistory.find(
        (item) => getQuarter(item.date) === targetQuarter
      );
      let confirmedOverwrite = false;
      if (existingVideoInQuarter) {
        confirmedOverwrite = window.confirm(
          `A video already exists for ${targetQuarter} in this package.\n` +
            `Existing: ${existingVideoInQuarter.name} (${new Date(
              existingVideoInQuarter.date
            ).toLocaleDateString()})\n\n` +
            `Do you want to update (replace) the old video with this new one?`
        );
        if (!confirmedOverwrite) {
          return;
        }
      }
      const validFilesToUpload = [];
      for (const file of selectedFiles) {
        let shouldAddFile = true;
        const isDuplicateLocal = uploadHistory.some(
          (historyItem) => historyItem.name === file.name
        );
        if (isDuplicateLocal) {
          const confirmedOverwrite = window.confirm(
            `"${file.name}" already exists in THIS package.\nOverwrite?`
          );
          if (!confirmedOverwrite) shouldAddFile = false;
        }
        if (shouldAddFile) {
          try {
            const duplicateInfo = await axiosInstance.get(
              `/api/pre-check/video?fileName=${file.name}`
            );
            const duplicates = duplicateInfo.data;
            if (Array.isArray(duplicates)) {
              // CASE A: It's an Array
              const otherDistrictDuplicate = duplicates.find(
                (d) => d.siteId && d.siteId.toString() !== pkg.toString()
              );
              if (otherDistrictDuplicate) {
                setToast({
                  open: true,
                  message: `Failed: "${file.name}" is already uploaded in ${
                    otherDistrictDuplicate.district || "another location"
                  }.`,
                  severity: "error",
                });
                shouldAddFile = false;
              }
            } else if (duplicates && typeof duplicates === "object") {
              // CASE B: It's an Object
              if (duplicates.exists && duplicates.packageId !== pkg) {
                setToast({
                  open: true,
                  message: `Failed: "${file.name}" is already uploaded in ${
                    duplicates.district || "another location"
                  }.`,
                  severity: "error",
                });
                shouldAddFile = false;
              }
            }
          } catch (error) {
            console.warn(
              "Duplicate check API failed (Status 500). Proceeding with upload...",
              error
            );
          }
        }
        if (shouldAddFile) {
          validFilesToUpload.push(file);
        }
      }

      const newItems = validFilesToUpload.map((f) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
        file: f,
        name: f.name,
        size: f.size,
        progress: 0,
        status: "queued",
      }));
      setFiles((prev) => [...prev, ...newItems]);
      newItems.forEach((item) => uploadFile(item));
    },
    [pkg, uploadDate, description, uploadHistory]
  );

  const packageOptions = useMemo(
    () => packages.filter((p) => p.districtId === district),
    [packages, district]
  );

  const displayedHistory = useMemo(() => {
    if (!district || !pkg) return [];
    return uploadHistory;
  }, [district, pkg, uploadHistory]);

  const getDistrictName = (id) =>
    districts.find((item) => item.id === id)?.name || id;
  const getPackageName = (id) =>
    packages.find((item) => item.id === id)?.displayName || id;

  const handleRemoveUploadingFile = (id) => {
    setFiles((prev) => prev.filter((p) => p.id !== id));
  };

  const handleView = (item) => {
    if (item.file) {
      const blobUrl = URL.createObjectURL(item.file);
      setSelectedCameraForModal({ camera_id: item.name, url: blobUrl });
      setModalOpen(true);
    } else if (item.url) {
      setSelectedCameraForModal({ camera_id: item.name, url: item.url });
      setModalOpen(true);
    } else {
      alert("Video URL not available.");
    }
  };

  const handleDownload = (item) => {
    if (item.file) {
      const blobUrl = URL.createObjectURL(item.file);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = item.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert("Direct download not supported for history items yet.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        await axiosInstance.delete(`/api/pre-check/video/${id}`);
        setUploadHistory((prev) => prev.filter((item) => item.id !== id));
        setToast({
          open: true,
          message: "Record deleted ",
          severity: "success",
        });
      } catch (error) {
        setToast({
          open: true,
          message: "Failed to delete record",
          severity: "error",
        });
      }
    }
  };

  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;

    const prevent = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onDrop = (e) => {
      prevent(e);
      el.style.borderColor = "#CBD5E1";
      el.style.backgroundColor = "#F8FAFC";
      if (e.dataTransfer?.files?.length) {
        handleFileSelection(Array.from(e.dataTransfer.files));
      }
    };
    const onDragOver = (e) => {
      prevent(e);
      el.style.borderColor = "#3B82F6";
      el.style.backgroundColor = "#EFF6FF";
    };
    const onDragLeave = (e) => {
      prevent(e);
      el.style.borderColor = "#CBD5E1";
      el.style.backgroundColor = "#F8FAFC";
    };

    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    el.addEventListener("dragenter", prevent);

    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
      el.removeEventListener("dragenter", prevent);
    };
  }, [handleFileSelection]);
  const handleCloseToast = () => setToast({ ...toast, open: false });

  if (loading)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  if (loadError)
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">Error: {loadError}</Typography>
      </Box>
    );

  const isSelectionComplete = district && pkg;

  return (
    <>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
          overflow: "hidden",
        }}
      >
        <Box sx={{ bgcolor: "#fff", flexShrink: 0 }}>
          <Box sx={{ px: 4, pt: 2, pb: 1 }}>
            <Typography
              component={Link}
              to="/"
              sx={{
                color: "#64748B",
                fontSize: "14px",
                display: "inline-flex",
                alignItems: "center",
                fontWeight: 600,
                textDecoration: "none",
                "&:hover": { color: "#1E293B" },
              }}
            >
              <ArrowLeft size={16} style={{ marginRight: 6 }} />
              Back to Dashboard
            </Typography>
          </Box>

          <Box
            sx={{
              height: "1px",
              width: "100%",
              bgcolor: "#E5E7EB",
              mb: 3,
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              px: 4,
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 900,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                ml: {
                  xs: 0,
                  sm: 5,
                  md: 21,
                  lg: 35,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "left",
                  gap: 1.5,
                  mb: 0.5,
                }}
              >
                <FolderOpen size={24} color="#3B82F6" />
                <Typography sx={{ fontSize: 20, fontWeight: 600 }}>
                  Select Package for Upload
                </Typography>
              </Box>

              <Typography
                sx={{
                  color: "#64748B",
                  fontSize: 13,
                  mb: 4,
                  textAlign: "center",
                }}
              >
                Choose the district and package where you want to upload and
                process videos
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  justifyContent: "center",
                  flexWrap: "wrap",
                  mb: isSelectionComplete ? 2 : 0,
                }}
              >
                <FormControl sx={{ minWidth: 300 }} size="small">
                  <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: 13 }}>
                    District
                  </Typography>
                  <Select
                    value={district}
                    onChange={(e) => {
                      setDistrict(e.target.value);
                      setPkg("");
                    }}
                    displayEmpty
                    sx={{ bgcolor: "#fff" }}
                  >
                    <MenuItem value="">Select District...</MenuItem>
                    {districts.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 300 }} size="small">
                  <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: 13 }}>
                    Package
                  </Typography>
                  <Select
                    value={pkg}
                    onChange={(e) => setPkg(e.target.value)}
                    displayEmpty
                    disabled={!district}
                    sx={{ bgcolor: "#fff" }}
                  >
                    <MenuItem value="">Select Package...</MenuItem>
                    {packageOptions.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            px: 4,
            pb: 4,
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {!isSelectionComplete ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box sx={{ textAlign: "center", maxWidth: 600 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    bgcolor: "#EFF6FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <Package size={40} color="#3B82F6" />
                </Box>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: "#1E293B",
                    mb: 1,
                  }}
                >
                  Select District and Package
                </Typography>
                <Typography sx={{ color: "#64748B", mb: 4, fontSize: 14 }}>
                  Choose a district and package above to start uploading videos
                  for AI-powered analysis.
                </Typography>

                <Paper
                  sx={{
                    bgcolor: "#EFF6FF",
                    border: "1px solid #BFDBFE",
                    borderRadius: 2,
                    p: 3,
                    textAlign: "left",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#1E40AF",
                      fontWeight: 600,
                      mb: 1,
                      fontSize: 14,
                    }}
                  >
                    What happens after upload?
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      pl: 2.5,
                      color: "#1E3A8A",
                      fontSize: 13,
                      lineHeight: 1.8,
                    }}
                  >
                    <li>AI analyzes post construction videos</li>
                    <li>Quality assessment and compliance detection</li>
                    <li>Automatic alert generation for issues</li>
                    <li>Integration with existing monitoring system</li>
                  </Box>
                </Paper>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  px: 2,
                  bgcolor: "#EFF6FF",
                  border: "1px solid #BFDBFE",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircle2 size={18} color="#3B82F6" />
                  <Typography
                    sx={{ fontWeight: 600, fontSize: 13, color: "#1E40AF" }}
                  >
                    Selected Package
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 3,
                    fontSize: 13,
                    color: "#1E3A8A",
                  }}
                >
                  <span>
                    District: <b>{getDistrictName(district)}</b>
                  </span>
                  <span>
                    Package: <b>{getPackageName(pkg)}</b>
                  </span>
                </Box>
              </Paper>

              <Grid
                container
                spacing={3}
                sx={{ flex: 1, overflow: "hidden", mt: 0 }}
              >
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={6}
                  sx={{
                    height: "100%",
                    width: "50vw",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1 }}>
                      Upload Configuration
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        borderColor: "#E2E8F0",
                        display: "flex",
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 0.5,
                          }}
                        >
                          <CalendarDays size={14} color="#64748B" />
                          <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                            Upload Date
                          </Typography>
                        </Box>
                        <TextField
                          type="date"
                          value={uploadDate}
                          onChange={(e) => setUploadDate(e.target.value)}
                          size="small"
                          sx={{ width: 160 }}
                          InputProps={{ style: { fontSize: 13 } }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 0.5,
                          }}
                        >
                          <FileText size={14} color="#64748B" />
                          <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                            Description
                          </Typography>
                        </Box>
                        <TextField
                          placeholder="e.g., Safety inspection"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          size="small"
                          fullWidth
                          InputProps={{ style: { fontSize: 13 } }}
                        />
                      </Box>
                    </Paper>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{ fontWeight: 600, fontSize: 14, color: "#334155" }}
                      >
                        Video Upload
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#94A3B8" }}>
                        MP4, AVI, MOV, MKV
                      </Typography>
                    </Box>
                    <Paper
                      ref={dropRef}
                      variant="outlined"
                      sx={{
                        flex: 1,
                        border: "2px dashed #CBD5E1",
                        borderRadius: 2,
                        bgcolor: "#F8FAFC",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "0.2s",
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "50%",
                          bgcolor: "#fff",
                          mb: 2,
                          boxShadow: "0px 1px 3px rgba(0,0,0,0.1)",
                        }}
                      >
                        <CloudUpload size={32} color="#64748B" />
                      </Box>
                      <Typography
                        sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}
                      >
                        Upload Post Construction Videos
                      </Typography>
                      <Typography
                        sx={{ color: "#64748B", fontSize: 12, mb: 2 }}
                      >
                        Drag and drop or click to browse
                      </Typography>
                      <Button
                        variant="contained"
                        component="label"
                        disableElevation
                        sx={{
                          bgcolor: "#2563EB",
                          textTransform: "none",
                          fontSize: 13,
                          px: 3,
                        }}
                      >
                        Choose Videos
                        <input
                          type="file"
                          multiple
                          hidden
                          onChange={(e) => {
                            if (e.target.files.length > 0) {
                              handleFileSelection(Array.from(e.target.files));
                            }
                          }}
                          accept="video/*"
                        />
                      </Button>
                      <Typography
                        sx={{ color: "#94A3B8", mt: 2, fontSize: 11 }}
                      >
                        Max: 500MB per video
                      </Typography>
                    </Paper>
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={3}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: "400px",
                    maxWidth: "600px",
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1 }}>
                    Upload History
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      flex: 1,
                      borderColor: "#E2E8F0",
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      bgcolor: "#fff",
                    }}
                  >
                    <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                      {files.map((f) => (
                        <Box
                          key={f.id}
                          sx={{
                            mb: 2,
                            p: 1.5,
                            borderRadius: 1,
                            border: "1px solid #E2E8F0",
                            bgcolor: "#F8FAFC",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography
                              noWrap
                              sx={{
                                fontSize: 12,
                                fontWeight: 600,
                                maxWidth: "70%",
                              }}
                            >
                              {f.name}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#64748B" }}>
                              {humanFileSize(f.size)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {f.status === "error" ? (
                              <Typography sx={{ fontSize: 11, color: "red" }}>
                                Upload Failed
                              </Typography>
                            ) : (
                              <LinearProgress
                                variant="determinate"
                                value={f.progress}
                                sx={{ flex: 1, height: 6, borderRadius: 3 }}
                              />
                            )}
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveUploadingFile(f.id)}
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                      {displayedHistory.length === 0 && files.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 8, opacity: 0.5 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              mb: 1,
                            }}
                          >
                            <History size={40} color="#94A3B8" />
                          </Box>
                          <Typography fontSize={12}>
                            No history found for this selection
                          </Typography>
                        </Box>
                      ) : (
                        displayedHistory.map((h) => (
                          <Box
                            key={h.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              py: 1.5,
                              borderBottom: "1px solid #F1F5F9",
                            }}
                          >
                            <FileVideo size={24} color="#3B82F6" />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                noWrap
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#1E293B",
                                }}
                              >
                                {h.name}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  mt: 0.5,
                                }}
                              >
                                <Typography
                                  sx={{ fontSize: 11, color: "#4B5563" }}
                                >
                                  {getDistrictName(h.districtId)} •{" "}
                                  {h.description || "No description"}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mt: 0.5,
                                  }}
                                >
                                  <Typography
                                    sx={{ fontSize: 11, color: "#64748B" }}
                                  >
                                    Package: {getPackageName(h.packageCode)}
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: 11, color: "#CBD5E1" }}
                                  >
                                    •
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: 11, color: "#64748B" }}
                                  >
                                    {new Date(h.date).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              {h.status === "success" && (
                                <Tooltip title="Uploaded">
                                  <div
                                    style={{ display: "flex", marginRight: 8 }}
                                  >
                                    <CheckCircle2 size={18} color="#16A34A" />
                                  </div>
                                </Tooltip>
                              )}
                              <Tooltip title="View">
                                <IconButton
                                  size="small"
                                  onClick={() => handleView(h)}
                                >
                                  <Eye size={18} color="#64748B" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownload(h)}
                                >
                                  <Download size={18} color="#64748B" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(h.id)}
                                >
                                  <Trash2 size={18} color="#EF4444" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        ))
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Box>
      <CameraModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedCamera={selectedCameraForModal}
      />
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
